'use client';

import posthog from 'posthog-js';

import { ANALYTICS_ENVIRONMENT } from '@cineamo/composables/src/providers/analytics-environment';

// PostHog's own `$device_type` is user-agent derived and disagrees with the breakpoint our layouts
// actually branch on: an iPad in landscape is exactly 1024px, so it renders the desktop layout while
// reporting "Tablet". Registered as a super property (not passed per event) so it also lands on
// `$feature_flag_called` — experiment results can only be split by a dimension the exposure carries.
const DESKTOP_MEDIA_QUERY = '(min-width: 1024px)';

const layoutOf = (isDesktop: boolean): string => (isDesktop ? 'desktop' : 'mobile');

/**
 * Registers the properties every PostHog event of ours carries, and keeps `layout` current.
 *
 * Shared by the apps so an experiment spanning both can be split by the same dimensions. Call it
 * synchronously after `posthog.init` rather than from an effect of its own: child effects run
 * before parent ones, so a deferred registration could lose the race with the first capture.
 *
 * `environment` matches the mobile app's super property of the same name, so all platforms can be
 * excluded from experiment results by a single PostHog test-account rule instead of a host regex.
 *
 * @returns the cleanup for the layout listener.
 */
export function registerPostHogSuperProperties(): () => void {
    const mediaQuery = window.matchMedia(DESKTOP_MEDIA_QUERY);

    function onLayoutChange(event: MediaQueryListEvent): void {
        posthog.register({ layout: layoutOf(event.matches) });
    }

    posthog.register({ environment: ANALYTICS_ENVIRONMENT, layout: layoutOf(mediaQuery.matches) });
    mediaQuery.addEventListener('change', onLayoutChange);

    return () => mediaQuery.removeEventListener('change', onLayoutChange);
}
